import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Pill } from "@/components/brand";
import { Input } from "@/components/ui/input";
import { Search, BookText, FileText, BookMarked, FileSignature, Link as LinkIcon,
  FolderOpen, Scale, ClipboardList, Building2, MessagesSquare, ArrowUpRight } from "lucide-react";

const TYPES = [
  { id: "all", label: "All", icon: BookText },
  { id: "faq", label: "FAQ Bank", icon: BookText },
  { id: "sop", label: "SOP Bank", icon: FileText },
  { id: "glossary", label: "Glossary", icon: BookMarked },
  { id: "template", label: "Templates", icon: FileSignature },
];

const SOURCES = [
  { id: "drive-sop", label: "SOP Bank", sub: "Google Drive folder", icon: FolderOpen, tone: "green",
    url: "https://drive.google.com/drive/folders/sop-bank" },
  { id: "faq-doc", label: "FAQ Bank", sub: "Google Doc", icon: MessagesSquare, tone: "cobalt",
    url: "https://docs.google.com/document/d/faq-bank" },
  { id: "templates", label: "Client Templates", sub: "Google Drive folder", icon: FileSignature, tone: "yellow",
    url: "https://drive.google.com/drive/folders/client-templates" },
  { id: "uspto", label: "USPTO Search", sub: "tmsearch.uspto.gov", icon: Scale, tone: "navy",
    url: "https://tmsearch.uspto.gov" },
  { id: "tsdr", label: "TSDR Status", sub: "tsdr.uspto.gov", icon: ClipboardList, tone: "navy",
    url: "https://tsdr.uspto.gov" },
  { id: "idmanual", label: "ID Manual", sub: "idm-tmng.uspto.gov", icon: BookMarked, tone: "cobalt",
    url: "https://idm-tmng.uspto.gov" },
  { id: "hubspot", label: "HubSpot", sub: "Client matters", icon: Building2, tone: "yellow",
    url: "https://app.hubspot.com/contacts" },
];

const SOURCE_ICON_BG = { green: "bg-[#DFF0E4] text-[#1E5B37]", cobalt: "bg-[#E1ECF7] text-[#1D57A5]",
  yellow: "bg-[#FFF3BF] text-[#8A6D00]", navy: "bg-[#E3E5F0] text-[#141B4D]" };

export default function Knowledge() {
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const params = {};
    if (type !== "all") params.type = type;
    if (q) params.q = q;
    api.get("/knowledge", { params }).then(({ data }) => setDocs(data));
  }, [type, q]);

  return (
    <div className="p-8 md:p-10">
      <PageHeader title="Knowledge Base" subtitle="The editable Knowledge Layer — SOPs, FAQ Bank, glossary & templates, synced from Google Drive. Never hardcode; always cite." />

      <section data-testid="knowledge-sources" className="mb-7">
        <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/45 mb-3">Jump to a source</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {SOURCES.map((s) => (
            <a key={s.id} href={s.url} target="_blank" rel="noreferrer" data-testid={`source-tile-${s.id}`}
              className="group bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${SOURCE_ICON_BG[s.tone]}`}>
                  <s.icon className="w-4.5 h-4.5" />
                </span>
                <ArrowUpRight className="w-4 h-4 text-[#101820]/25 group-hover:text-[#1D57A5] transition-colors" />
              </div>
              <div>
                <div className="font-bold text-sm text-[#141B4D] leading-tight">{s.label}</div>
                <div className="text-[11px] text-[#101820]/45 mt-0.5">{s.sub}</div>
              </div>
            </a>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#101820]/40" />
          <Input data-testid="knowledge-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search SOPs, FAQ & glossary… (e.g. 'is it 5 or 10 years?')" className="pl-9 h-11 bg-white border-[#BDC5DB]" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {TYPES.map((t) => (
            <button key={t.id} data-testid={`knowledge-type-${t.id}`} onClick={() => setType(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${type === t.id ? "bg-[#141B4D] text-white" : "bg-white text-[#141B4D] border border-[#BDC5DB]"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {docs.map((d) => (
          <div key={d.id} data-testid={`knowledge-${d.id}`} className="bg-white rounded-xl p-5 shadow-sm border border-black/5 card-lift">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-[#141B4D]">{d.title}</h3>
              <Pill tone="cobalt">{d.type.toUpperCase()}</Pill>
            </div>
            <p className="text-sm text-[#101820]/80 mt-2 leading-relaxed">{d.body}</p>
            {d.links?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {d.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer" data-testid={`knowledge-link-${d.id}-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1D57A5] bg-[#E1ECF7] rounded-lg px-2.5 py-1.5 hover:bg-[#1D57A5] hover:text-white transition-all">
                    <LinkIcon className="w-3.5 h-3.5" /> {l.label}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-[#101820]/40">Source: {d.source}</span>
              {d.tags?.map((t) => <Pill key={t} tone="pale">{t}</Pill>)}
            </div>
          </div>
        ))}
        {docs.length === 0 && <div className="text-[#101820]/50">Nothing found. Try a different search.</div>}
      </div>
    </div>
  );
}
